import { Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { hexToRGBA } from 'lib/designSystem/BarChart';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import EnhacedBox from '../Box';

interface SimpleBarChartProps {
  data:
    | {
        label: string;
        value: number;
      }[]
    | Record<string, number>;
  title?: string;
  icon?: React.ReactNode;
  color: string;
  direction?: 'horizontal' | 'vertical';
  height?: number;
  xAxisLabel?: string;
  yAxisLabel?: string;
  valueFormatter?: (value: number | null) => string;
  maxItems?: number;
  sortBy?: 'asc' | 'desc';
  showValueLabels?: boolean;
  customMargin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  barColors?: string[];
  showGrid?: boolean;
  showLegend?: boolean;
  customHeight?: number;
}

export const convertRecordToChartData = (
  data: Record<string, number>,
  maxItems?: number,
  sortBy: 'asc' | 'desc' = 'desc',
): { label: string; value: number }[] => {
  if (!data) return [];
  const entries = Object.entries(data);
  let sortedEntries = [...entries];
  if (sortBy === 'desc') sortedEntries.sort(([, a], [, b]) => b - a);
  else sortedEntries.sort(([, a], [, b]) => a - b);
  if (maxItems) sortedEntries = sortedEntries.slice(0, maxItems);

  return sortedEntries.map(([label, value]) => ({ label, value }));
};

const SimpleBarChart = ({
  data,
  title,
  icon,
  color,
  direction = 'vertical',
  xAxisLabel,
  yAxisLabel,
  valueFormatter,
  maxItems = 10,
  sortBy = 'desc',
  barColors,
  customMargin = { left: 120, right: 0, top: 0, bottom: 20 },
  customHeight = 400,
}: SimpleBarChartProps) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);

  const processedData = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object')
      return convertRecordToChartData(data, maxItems, sortBy);
    return [];
  }, [data, maxItems, sortBy]);

  const chartData = useMemo(() => {
    return processedData.map((item, index) => ({
      id: index,
      label: item.label,
      value: item.value,
    }));
  }, [processedData]);

  const defaultValueFormatter = (value: number | null) => {
    if (value === null) return '';
    return valueFormatter ? valueFormatter(value) : value.toString();
  };

  useEffect(() => {
    const updateDimensions = () => {
      if (elementRef.current) {
        const { width } = elementRef.current.getBoundingClientRect();
        setHeight(Math.min(customHeight, width * 0.4));
        setWidth(width - 40);
      }
    };

    updateDimensions();

    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  return (
    <EnhacedBox small ref={elementRef}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: hexToRGBA(color, 0.12),
          }}
        >
          {icon}
        </div>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1.1rem',
          }}
        >
          {title}
        </Typography>
      </div>
      <BarChart
        dataset={chartData}
        xAxis={
          direction === 'horizontal'
            ? [
                {
                  scaleType: 'linear',
                  label: xAxisLabel,
                },
              ]
            : [
                {
                  dataKey: 'label',
                  scaleType: 'band',
                  label: xAxisLabel,
                  tickLabelStyle: {
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: 80,
                  },
                },
              ]
        }
        yAxis={
          direction === 'horizontal'
            ? [
                {
                  dataKey: 'label',
                  scaleType: 'band',
                  label: yAxisLabel,
                },
              ]
            : [
                {
                  scaleType: 'linear',
                  label: yAxisLabel,
                },
              ]
        }
        series={[
          {
            dataKey: 'value',
            valueFormatter: defaultValueFormatter,
            color: barColors?.[0] || color,
          },
        ]}
        layout={direction}
        height={height}
        margin={customMargin}
        width={width}
      />
    </EnhacedBox>
  );
};

export default SimpleBarChart;
